import { convertToTranslationKey, processSpecialCharacters } from '@heidi/utils';
import { useQueryUser } from '@heidi/workspace/data';
import { useQuerySpecialties } from './query/useQuerySpecialties';

/**
 * Resolves the current user's specialty as its canonical catalog token
 * (`GeneralPractitioner`, `GeneralSurgeon`), derived from the stable English
 * `name` (`General Practitioner`) rather than the translated `localizedName`.
 * The token is the same PascalCase key the specialty translation catalog uses.
 *
 * For logic keyed on the specialty rather than display: a translated name
 * would read differently in every language. Use `useUserSpecialtyName` to
 * show the specialty to the clinician.
 *
 * `undefined` while either query is loading or has failed, when the user has
 * no `specialty_id`, or when it matches nothing in the list. A free-text
 * `other_specialty` has no canonical token, so it resolves to `undefined` too.
 */
export function useUserCanonicalSpecialtyName(): string | undefined {
  const { data: specialtyId } = useQueryUser((data) => data?.specialty_id);
  const { data: specialties } = useQuerySpecialties((data) => data.specialties);

  if (!specialtyId || !specialties) return undefined;
  const name = specialties.find((specialty) => specialty.id === specialtyId)?.name;
  return name ? convertToTranslationKey(processSpecialCharacters(name)) : undefined;
}
